// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@turnerBlueprint/App/Components/Common/XngZone";
import { checkoutPages } from "@turnerBlueprint/XDL/constants";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // XNG-Change
    rightSide?: GridItemProps;
    rightSideContainer?: GridContainerProps;
    copyrightText?: TypographyPresentationProps;
    subscribeContainer?: InjectableCss;
    socialLinks?: InjectableCss;
    copyrightContainer?: InjectableCss;
}

export const footerContainerStyles: FooterContainerStyles = {
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 2, 2],
    },
    subscribeGridItem: {
        width: [12, 12, 8, 4, 4],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    const isCheckoutPage = useAppSelector(state => checkoutPages.includes(getCurrentPage(state).type));
    // XNG-Change
    const isEditingInShell = useShellContext().isEditing;
    // XNG-Change: Restructure
    return (
        <GridContainer {...styles.widgetGridContainer}>
            {!isCheckoutPage && (
                <StyledWrapper {...styles.subscribeContainer}>
                    <XngZone zoneName="Subscribe" hint="Subscribe" contentId={id} />
                    <StyledWrapper {...styles.socialLinks}>
                        <XngZone zoneName="SocialLinks" hint="Social Links" contentId={id} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.logoWrapper} className={`${isEditingInShell ? "CMS" : ""}`}>
                        <XngZone zoneName="Logo" hint="Logo" contentId={id} />
                    </StyledWrapper>
                </StyledWrapper>
            )}

            <StyledWrapper {...styles.rightSide}>
                {!isCheckoutPage && (
                    <GridContainer {...styles.rightSideContainer}>
                        <GridItem {...styles.linkList1GridItem}>
                            <XngZone zoneName="LinkList1" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkList2GridItem}>
                            <XngZone zoneName="LinkList2" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkList3GridItem}>
                            <XngZone zoneName="LinkList3" contentId={id} />
                        </GridItem>
                        <GridItem {...styles.linkList4GridItem}>
                            <XngZone zoneName="LinkList4" contentId={id} />
                        </GridItem>
                    </GridContainer>
                )}

                <StyledWrapper {...styles.copyrightContainer}>
                    <Typography {...styles.copyrightText} {...{ isCheckoutPage }}>
                        {siteMessage(`Custom_Footer_Copyright`, String(new Date().getFullYear()))}
                    </Typography>
                    {(isEditingInShell || isCheckoutPage) && (
                        <StyledWrapper {...styles.siteMapLinksGridItem}>
                            <XngZone hint="Site Map Links" zoneName="SiteMapLinks" contentId={id} />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </GridContainer>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
    },
};

export default footerContainer;
